export const getDateRange = (start: string, end: string) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };

  return `${startDate.toLocaleTimeString(
    'en-US',
    options
  )} - ${endDate.toLocaleTimeString('en-US', options)}`;
};

export const getTimeDifference = (isoString: string): string => {
  const currentTime = new Date();
  const outreachTime = new Date(isoString);
  const timeDifference = Math.abs(
    currentTime.getTime() - outreachTime.getTime()
  );
  const minutes = Math.floor(timeDifference / 1000 / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''}`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  } else {
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  }
};

export const formatDate = (isoString: string): string => {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export const getTimeSinceDate = (
  isoStringStart: string,
  isoStringEnd?: string
): string => {
  const date = new Date(isoStringEnd || Date.now());
  const now = new Date(isoStringStart);
  const absMs = Math.abs(now.getTime() - date.getTime());
  const dayMs = 1000 * 60 * 60 * 24;
  const days = Math.floor(absMs / dayMs);
  const hourMs = 1000 * 60 * 60;
  const hours = Math.floor(absMs / hourMs);

  if (hours < 1) {
    const minuteMs = 1000 * 60;
    const minutes = Math.floor(absMs / minuteMs);
    return `${minutes < 1 ? 1 : minutes}m ago`;
  }

  if (days < 1) {
    return `${hours}h ago`;
  }

  if (days < 7) {
    return `${days}d ago`;
  }

  if (days < 30) {
    return `${Math.floor(days / 7)}w ago`;
  }

  if (days < 365) {
    return `${Math.floor(days / 30)}mo ago`;
  }

  if (days < 730) {
    return `${Math.floor(days / 365)}y ago`;
  }

  return '>1y ago';
};

export const formatDateOrdinal = (date: string | number): string => {
  const dateInstance = new Date(date);
  const weekday = dateInstance.toLocaleDateString('en-US', { weekday: 'long' });
  const month = dateInstance.toLocaleDateString('en-US', { month: 'long' });
  const day = dateInstance.getDate();

  const suffix =
    day % 10 === 1 && day !== 11
      ? 'st'
      : day % 10 === 2 && day !== 12
        ? 'nd'
        : day % 10 === 3 && day !== 13
          ? 'rd'
          : 'th';

  return `${weekday}, ${month} ${day}${suffix}`;
};

export const formatDateTime = (isoString: string): string => {
  const date = new Date(isoString);
  const now = new Date();

  const isToday = now.toDateString() === date.toDateString();

  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
  });

  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });

  if (isToday) {
    const formattedDate = dateFormatter.format(date);
    const formattedTime = timeFormatter.format(date);
    return `Today, ${formattedDate}, ${formattedTime}`;
  } else {
    const formattedDate = dateFormatter.format(date);
    const formattedTime = timeFormatter.format(date);

    return `${date.toLocaleDateString('en-US', {
      weekday: 'long',
    })}, ${formattedDate}, ${formattedTime}`;
  }
};

export const formatDateAge = (date: string) => {
  const inputDate = new Date(date);
  const currentDate = new Date();

  const yearDifference =
    (currentDate.getTime() - inputDate.getTime()) /
    (1000 * 60 * 60 * 24 * 365.25);

  if (yearDifference > 2) {
    return `${Math.floor(yearDifference)}+ yr old`;
  } else {
    return `Installed ${inputDate.getUTCFullYear()}`;
  }
};

export const calculateYearsBetween = (
  isoString1: string,
  isoString2: string
) => {
  const d1 = new Date(isoString1);
  const d2 = new Date(isoString2);

  const startYear = d1.getFullYear();
  const endYear = d2.getFullYear();
  const startMonth = d1.getMonth();
  const endMonth = d2.getMonth();
  const startDay = d1.getDate();
  const endDay = d2.getDate();

  let years = endYear - startYear;

  if (endMonth < startMonth || (endMonth === startMonth && endDay < startDay)) {
    years--;
  }

  return years;
};

export const formatMeridianTime = (date: string | number): string => {
  const dateInstance = new Date(date);
  return dateInstance.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
};

export const getTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getDateTz =
  (...date: Parameters<typeof Date.UTC>) =>
  (
    options?: Partial<
      { locale: Intl.LocalesArgument } & Intl.DateTimeFormatOptions
    >
  ) =>
    new Date(
      new Date(Date.UTC(...date)).toLocaleString(options?.locale || 'en-US', {
        timeZone: options?.timeZone || getTimeZone(),
      })
    );

export const getEndOfDay = (date: Date = new Date()): Date =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999
  );

export const getStartOfDayForTz = (
  year: number,
  monthIndex: number,
  day: number,
  options?: Partial<{ timeZone: string }>
) => {
  const readIn = getDateTz(year, monthIndex, day);
  const offsetFromUtc =
    readIn({ timeZone: options?.timeZone }).getTime() -
    readIn({ timeZone: 'UTC' }).getTime();

  return new Date(Date.UTC(year, monthIndex, day) - offsetFromUtc);
};

export const getEndOfDayForTz = (
  year: number,
  monthIndex: number,
  day: number,
  options?: Partial<{ timeZone: string }>
) =>
  new Date(
    getStartOfDayForTz(year, monthIndex, day + 1, options).getTime() - 1
  );

export const isDateInFuture = (
  date: Date | string,
  dateToCheck: Date | string = new Date()
) => {
  const targetDate = new Date(date);
  const now = new Date(dateToCheck);
  return targetDate >= now;
};

export const isDateInRange = (
  date: Date | string,
  startDateTime: Date | string,
  endDateTime: Date | string
) => {
  const targetDate = new Date(date);
  const rangeStart = new Date(startDateTime);
  const rangeEnd = new Date(endDateTime);

  return targetDate >= rangeStart && targetDate < rangeEnd;
};

export const getFormInputDate = (
  date?: Date,
  options?: Partial<{ timeZone: string }>
) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: options?.timeZone ?? getTimeZone(),
  }).format(date ?? new Date());

export const formatDateRangeShort = (
  start: Date | string | number,
  end: Date | string | number,
  options?: Partial<{ timeZone: string }>
) => {
  const timeZone = options?.timeZone ?? getTimeZone();
  const startDate = new Date(start);
  const endDate = new Date(end);

  const dayWithMonth = (date: Date) =>
    date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone,
    });

  if (startDate.getTime() === endDate.getTime()) {
    return dayWithMonth(startDate);
  }

  const monthOf = (date: Date) =>
    date.toLocaleDateString('en-US', { month: 'short', timeZone });

  if (monthOf(startDate) !== monthOf(endDate)) {
    return `${dayWithMonth(startDate)} - ${dayWithMonth(endDate)}`;
  }

  const endDay = endDate.toLocaleDateString('en-US', {
    day: 'numeric',
    timeZone,
  });

  return `${dayWithMonth(startDate)}-${endDay}`;
};

export const parseFormInputDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export type TimeUnits = 'M' | 'w' | 'd' | 'h' | 'm' | 's' | 'ms';

export const addToDate = (
  number: number,
  unit: TimeUnits,
  date: Date | string = new Date()
) => {
  const newDate = new Date(date);

  const updateFns: Record<TimeUnits, () => void> = {
    M: () => {
      const expectedMonth = (newDate.getMonth() + number + 12) % 12;
      newDate.setMonth(newDate.getMonth() + number);
      if (newDate.getMonth() !== expectedMonth) {
        newDate.setDate(0);
      }
    },
    w: () => newDate.setDate(newDate.getDate() + number * 7),
    d: () => newDate.setDate(newDate.getDate() + number),
    h: () => newDate.setHours(newDate.getHours() + number),
    m: () => newDate.setMinutes(newDate.getMinutes() + number),
    s: () => newDate.setSeconds(newDate.getSeconds() + number),
    ms: () => newDate.setMilliseconds(newDate.getMilliseconds() + number),
  };

  updateFns[unit]();

  return newDate;
};
